import { Injectable } from '@angular/core';
import { ApiService } from '../../../../_core/_services/api.service';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PortalManagementSubscriptionService {
  constructor(private api: ApiService) {}

  getPortalSubscriptions(queryParams?: any) {
    if (queryParams) {
      queryParams = {
        ...queryParams
      };
    } else {
      queryParams = {
        limit: 100,
        offset: 0,
        q: '',
        filter: []
      };
    }

    return this.api.getAPI('portal/management/subscriptions', queryParams).pipe(
      map((res: any) => {
        console.log('res subs ', res);
        return res.body;
      })
    );
  }

  getPortalSubscriptionByCustomer(portalCustomerId: number) {
    return this.api
      .getAPI(`portal/management/subscriptions/customer/${portalCustomerId}`)
      .pipe(map((res: any) => res.body.data));
  }

  getPortalSubscriptionStatistics(startDate?: string | null, endDate?: string | null) {
    let params: any = {};

    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return this.api
      .getAPI(`portal/management/statistics/subscriptions`, params)
      .pipe(map((res: any) => res.body));
  }

  getPortalManagementSubscriptionById(portalSubscriptionId: number) {
    return this.api
      .getAPI(`portal/management/subscriptions/${portalSubscriptionId}`)
      .pipe(map((res: any) => res.body));
  }

  requestSubscriptionOverride(subscriptionId: string, data: any): Observable<any> {
    return this.api.getAPI(`portal/management/subscriptions/${subscriptionId}/override`, data).pipe(
      map((res: any) => {
        return res.body;
      })
    );
  }

  requestSubscriptionReactivate(subscriptionId: string, data: any): Observable<any> {
    return this.api
      .getAPI(`portal/management/subscriptions/${subscriptionId}/reactivate`, data)
      .pipe(
        map((res: any) => {
          return res.body;
        })
      );
  }

  // Manually re-runs the external TECH8 webhook call for a subscription
  // lifecycle action (activate/reactivate/deactivate) - unlike
  // requestSubscriptionReactivate above, this is awaited server-side and
  // returns the real outcome. Used to retry a failed workflow entry from
  // the account-centre Activities timeline.
  retrySubscriptionWorkflow(subscriptionId: number, actionType: string): Observable<any> {
    return this.api
      .postAPI(`portal/management/subscriptions/${subscriptionId}/workflows/${actionType}/retry`, {})
      .pipe(map((res: any) => res.body));
  }

  // Manually suspends a subscription for non-payment - applies the same
  // status transition and TECH8 deactivate webhook call the daily billing
  // cron applies automatically once an account is overdue, on demand.
  suspendSubscription(subscriptionId: number): Observable<any> {
    return this.api
      .postAPI(`portal/management/subscriptions/${subscriptionId}/suspend`, {})
      .pipe(map((res: any) => res.body));
  }

  // Adds a subscription (a new line) to an existing customer - unlike the
  // customer self-service signup endpoint, this doesn't reject a customer
  // who already has a subscription, so it's how staff give an existing
  // customer a genuine second/third line on the same account.
  createSubscription(payload: any): Observable<any> {
    return this.api
      .postAPI('portal/management/subscriptions/create', payload)
      .pipe(map((res: any) => res.body));
  }

  changeSubscriptionDevice(subscriptionId: number, newDeviceSerialNo: string): Observable<any> {
    return this.api
      .postAPI(`portal/management/subscriptions/${subscriptionId}/change-device`, {
        new_device_serial_no: newDeviceSerialNo
      })
      .pipe(
        map((res: any) => {
          return res.body;
        })
      );
  }

  claimCircuitForSubscription(subscriptionId: number, deviceReference: string): Observable<any> {
    return this.api
      .postAPI(`portal/management/subscriptions/${subscriptionId}/claim-circuit`, {
        device_reference: deviceReference
      })
      .pipe(
        map((res: any) => {
          return res.body;
        })
      );
  }

  changeSubscriptionAccountReference(
    subscriptionId: number,
    newAccountReference: string
  ): Observable<any> {
    return this.api
      .patchAPI(`portal/management/subscriptions/${subscriptionId}/account-reference`, {
        new_account_reference: newAccountReference
      })
      .pipe(
        map((res: any) => {
          return res.body;
        })
      );
  }

  downloadSubscriptionStatement(subscriptionId: string): Observable<any> {
    return this.api.getFileAPI(`portal/management/subscriptions/${subscriptionId}/statement`);
  }

  updatePortalSubscription(portalSubscriptionId: number, portalSubscriptionUpdate: any) {
    return this.api.patchAPI(
      `portal/management/subscriptions/update/${portalSubscriptionId}`,
      portalSubscriptionUpdate
    );
  }

  upgradePortalSubscription(
    portalSubscriptionId: number,
    portalUpgradePackageId: number,
    applyImmediately = false
  ) {
    return this.api.getAPI(
      `portal/management/subscriptions/regrade/${portalSubscriptionId}/product/${portalUpgradePackageId}`,
      { apply_immediately: applyImmediately }
    );
  }

  addPortalSubscriptionNote(portalSubscriptionId: number, note: string) {
    return this.api
      .postAPI(`portal/management/subscriptions/${portalSubscriptionId}/notes`, {
        note: note
      })
      .pipe(map((res: any) => res.body.data));
  }

  getPortalSubscriptionNotes(portalSubscriptionId: number) {
    return this.api
      .getAPI(`portal/management/subscriptions/${portalSubscriptionId}/notes`)
      .pipe(map((res: any) => res.body.data));
  }

  removeRecurringPayment(portalSubscriptionId: number) {
    return this.api.postAPI(
      `portal/management/subscriptions/${portalSubscriptionId}/remove-recurring-payment`,
      {}
    );
  }

  getEndCustomerCircuit(circuitId: string) {
    return this.api
      .getAPI(`portal/management/subscriptions/${circuitId}/circuit`)
      .pipe(map((res: any) => res.body.data));
  }

  cancelSubscription(portal_transaction_id: number, reason: any) {
    return this.api
      .postAPI(`portal/management/subscriptions/${portal_transaction_id}/cancel`, reason)
      .pipe(
        map((res: any) => {
          return res.body;
        })
      );
  }

  processAgentCommission(portal_subscription_id: number, data: any) {
    return this.api
      .patchAPI(`portal/management/subscriptions/${portal_subscription_id}/agent-commission`, data)
      .pipe(
        map((res: any) => {
          return res.body;
        })
      );
  }
}
